/**
 * DIT-515 — the two statements a plan card can make, and why they are not one.
 *
 * Blue used to mean `plan.popular`, which reads as "we recommend this" on the
 * public pricing page and as "this is the one you picked" inside the signup
 * funnel. So Time Basic — the one plan flagged `popular` — stayed blue whatever
 * a visitor clicked, and the thin ring the chosen card got was no match for a
 * whole card of colour.
 *
 * These live here, apart from the component, so the rule can be tested as the
 * rule rather than as a copy of it kept beside a test.
 */

/** Whether this card wears the blue treatment. */
export function wearsHighlight(plan: { popular?: boolean }, highlighted?: boolean): boolean {
  // Undefined means the caller has no notion of selection — every marketing
  // surface — so the recommendation keeps the colour there, unchanged.
  return highlighted ?? Boolean(plan.popular);
}

/**
 * Whether this card wears the gold "Popular" badge.
 *
 * Keyed on the plan and nothing else: choosing Project Pro does not make Project
 * Pro the recommendation, and the recommended card keeps saying so while somebody
 * else's card is blue.
 */
export function wearsPopularBadge(plan: { popular?: boolean }): boolean {
  return Boolean(plan.popular);
}

/**
 * What the funnel hands the card as `highlighted`.
 *
 * The rules above are only half the change: the other half is that the funnel
 * passes its selection. Getting it wrong looks like this —
 * `highlighted={plan.popular}` on the plan step — and puts the original bug back
 * with every rule above still returning the right answer for the input it is
 * given.
 *
 * That is guarded by `pages/plan/ui/PlanTile.test.tsx` and `PlanPage.test.tsx`,
 * which render the tile and the step, and **not** by the tests beside this file:
 * composing an identity function with `wearsHighlight` pins the rule and says
 * nothing about the call site. The first version of this comment claimed
 * coverage that did not exist, and the review caught it by putting
 * `plan.popular` back and watching the suite stay green.
 *
 * Reading surfaces — public pricing, the industry pages, anywhere a plan is read
 * rather than picked — have no counterpart here on purpose: they pass nothing,
 * and `wearsHighlight` leaves the recommendation its colour.
 */
export const funnelHighlight = (isSelected: boolean): boolean => isSelected;

/**
 * Which plan is *the* recommendation — the one the funnel opens with selected.
 *
 * Returns `undefined` when nothing is flagged, and that is the important half.
 * The tempting fallback is "then take the first plan", which would silently
 * pre-select Time Zero — the free one — and quietly open the paid funnel on a
 * plan that charges nothing. No recommendation is a real state and the page
 * handles it: it simply opens with nothing selected.
 *
 * The first flagged plan wins, so the order the caller passes them in decides
 * the answer if two are ever flagged. Only one is today, and a second would
 * still show two gold badges, which is the visible half of the same mistake.
 */
export function recommendedPlanId<T extends { id: string; popular?: boolean }>(
  plans: readonly T[],
): string | undefined {
  return plans.find((plan) => plan.popular)?.id;
}
